/**
 * Title Component
 *
 * Displays a page or section title with optional subtitle and icon.
 * Supports template variable resolution for dynamic content.
 * Rendered as an attractive banner-style component.
 */

import { Image } from '@heroui/react';
import { Icon } from '@iconify/react';
import NextImage from 'next/image';

import { TitleComponentProps } from '@/@cms/types/props';

/**
 * Title component for page headers and section headings
 *
 * Features:
 * - Main title with optional subtitle in banner format
 * - Icon support (either from IconName enum or custom image)
 * - Template variable resolution
 * - Banner-style design with gradient background
 * - Responsive design with appropriate spacing
 *
 * @param props - Component properties from the CMS
 * @returns Rendered Title banner component
 */
const TitleComponent = ({
    data,
    resolveTemplate,
    className = '',
    isLoading = false,
}: TitleComponentProps) => {
    // Process template variables if resolver is provided
    const title = resolveTemplate ? resolveTemplate(data.Title) : data.Title;
    const subtitle = data.Subtitle
        ? resolveTemplate
            ? resolveTemplate(data.Subtitle)
            : data.Subtitle
        : '';

    // Determine icon rendering approach - either IconName or custom image

    const hasIconName = Boolean(data.Icon) && data.Icon !== 'None';
    const hasIconAlternate = Boolean(data.IconAlternate) && data.IconAlternate?.url !== 'None';

    // Show loading state if needed
    if (isLoading) {
        return (
            <div className={`title-banner-component animate-pulse ${className}`}>
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-gray-200 to-gray-300 px-8 py-12 shadow-lg">
                    <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-full bg-gray-300"></div>
                        <div className="space-y-3">
                            <div className="h-8 w-64 rounded bg-gray-300"></div>
                            <div className="h-4 w-48 rounded bg-gray-300"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`title-banner-component ${className}`}>
            <div className="relative overflow-hidden rounded-2xl border border-divider bg-gradient-to-r from-primary-100 via-primary-50 to-secondary-100 px-8 py-8 shadow-lg dark:border-divider dark:from-primary-900/20 dark:via-primary-800/20 dark:to-secondary-900/20">
                {/* Background decoration */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent dark:from-white/5"></div>

                {/* Content */}
                <div className="relative z-10 flex items-center gap-6">
                    {/* Icon rendering */}
                    {hasIconName && (
                        <div className="flex-shrink-0 rounded-full bg-background/70 p-3 shadow-md dark:bg-gray-800/70">
                            <Icon
                                icon={`lucide:${data.Icon}`}
                                width={40}
                                height={40}
                                className="text-primary-600 dark:text-primary-400"
                            />
                        </div>
                    )}
                    {!hasIconName && (
                        <div className="flex-shrink-0 rounded-full bg-background/70 p-4 shadow-md dark:bg-gray-800/70">
                            <Image
                                as={NextImage}
                                src={
                                    hasIconAlternate && data.IconAlternate?.url
                                        ? data.IconAlternate.url
                                        : '/assets/images/MFourLogo.svg'
                                }
                                alt={data.IconAlternate?.alternativeText || 'Title icon'}
                                width={40}
                                height={40}
                                className="rounded-none object-contain"
                            />
                        </div>
                    )}

                    {/* Text content */}
                    <div className="flex-1 space-y-2">
                        {/* Main title */}
                        <h1 className="text-2xlarge font-bold text-foreground">{title}</h1>
                        {/* Subtitle */}
                        {subtitle && <p className="text-medium text-foreground/80">{subtitle}</p>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TitleComponent;
